import { ReactComponent as TrashIcon } from "../../assets/icons/tables/trash-icon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/tables/edit-icon.svg";
import { useState } from "react";
import { DeleteModal } from "../../components";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { IVoucher, useVouchers } from "../../store/useVouchers";
import { useNavigate } from "react-router";

dayjs.extend(relativeTime);

interface VouchersTableProps {
  onEditVoucher?: (voucher: IVoucher) => void;
}

export const VouchersTable = ({ onEditVoucher }: VouchersTableProps) => {
  /** Hooks */
  const navigate = useNavigate();
  const { allVouchers, deleteVoucher } = useVouchers();
  /** State */
  const [isOpen, setIsOpen] = useState(false);

  const toggleModal = () => setIsOpen(!isOpen);

  const resolveUsageLabel = (voucher: IVoucher) => {
    if (voucher?.sourceType) {
      if (voucher.sourceType === "LOTTERY") return "Lottery";
      if (voucher.sourceType === "CHALLENGE") return "Challenge";
      return "Unknown";
    }
    if (voucher?.lottery) return "Lottery";
    if (voucher?.challenge) return "Challenge";
    return "Unknown";
  };

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
        <thead className="font-medium bg-gray-50">
          <tr>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Code
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Value
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Usage
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Instructions
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Benefit Text
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Expire Date
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Status
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Redeemed
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {allVouchers?.map((item) => {
            return (
              <tr
                key={item._id}
                onClick={() => navigate(`/voucher-details/${item._id}`)}
                className="bg-white hover:bg-gray-100 transition ease-linear border-b cursor-pointer"
              >
                <td className="px-2 sm:px-6 py-1 sm:py-4 break-all sm:whitespace-nowrap">
                  {item?.code}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4">
                  {item?.value || "No value"}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4">
                  {resolveUsageLabel(item)}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4 max-w-xs truncate">
                  {item?.instructions || "No instructions"}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4 max-w-xs truncate">
                  {item?.benefitText || "No benefit text"}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                  {item?.expireDate
                    ? dayjs(item.expireDate).format("DD/MM/YYYY")
                    : "No expire date"}
                </td>
                <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                  {item?.status}
                </td>
                <td
                  className={`hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4 ${Array.isArray(item?.redeemedBy) && item.redeemedBy.length > 0
                    ? "text-green-500"
                    : "text-red-500"
                    }`}
                >
                  {Array.isArray(item?.redeemedBy) && item.redeemedBy.length > 0
                    ? item.redeemedBy.length
                    : "Not redeemed"}
                </td>
                <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end cursor-pointer">
                  {onEditVoucher && (
                    <EditIcon
                      className="mr-3 sm:mr-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditVoucher(item);
                      }}
                    />
                  )}
                  <TrashIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      const confirmDelete = window.confirm(
                        "Are you sure you want to delete this Voucher?"
                      );
                      if (confirmDelete) {
                        deleteVoucher(item._id as string);
                      }
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <DeleteModal isOpen={isOpen} closeModal={toggleModal} />
    </div>
  );
};
